<script setup lang="ts">
definePageMeta({
  colorMode: 'dark'
})

const { data: page } = await useAsyncData('index', () => queryCollection('content').first())
if (!page.value) {
  throw createError({ statusCode: 404, statusMessage: 'Page not found', fatal: true })
}

const title = page.value?.seo?.title || page.value?.title
const description = page.value?.seo?.description || page.value?.description

// Récupère le User-Agent côté serveur, ou côté client si on navigue côté client
const headers = useRequestHeaders(['user-agent'])
const userAgent = headers['user-agent'] || (import.meta.client ? navigator.userAgent : '')

// Regex basique pour détecter les bots SEO et les outils de perf (Lighthouse/PageSpeed)
const isBot = /bot|googlebot|crawler|spider|robot|crawling|lighthouse|chrome-lighthouse/i.test(userAgent)

useSeoMeta({
  title,
  ogTitle: title,
  description,
  ogDescription: description
})
useHead({
  script: [
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Electrician',
        'name': 'Ohmelec',
        'image': 'https://ohmelec.net/logo.webp',
        'url': 'https://ohmelec.net',
        'description': 'Expert en électricité générale et sécurité intervenant en occitanie.',
        'address': {
          '@type': 'PostalAddress',
          'addressLocality': 'Montpellier',
          'addressRegion': 'Occitanie',
          'addressCountry': 'FR'
        },
        'geo': {
          '@type': 'GeoCoordinates',
          'latitude': 43.610769,
          'longitude': 3.876716
        },
        'priceRange': '€€',
        'areaServed': [
          { '@type': 'City', 'name': 'Montpellier' },
          { '@type': 'City', 'name': 'Nîmes' },
          { '@type': 'City', 'name': 'Alès' },
          { '@type': 'City', 'name': 'Lunel' },
          { '@type': 'City', 'name': 'Sommières' }
        ]
      })
    }
  ]
})
const heroTitle = computed(() => {
  const [primary = '', ...secondaryParts] = (page.value?.title ?? '').split('\n')

  return {
    primary,
    secondary: secondaryParts.join(' ').trim()
  }
})

const isPhoneRevealed = ref(false)
const { copy, copied } = useClipboard()

const phoneLabel = computed(() => {
  if (copied.value) return 'Numéro copié !'
  if (isPhoneRevealed.value) return page.value?.cta.command // Affiche le vrai numéro
  return 'Voir le numéro' // Texte par défaut (anti-robots)
})

function handlePhoneClick() {
  const rawNumber = page.value?.cta.command || ''
  const cleanNumber = rawNumber.replace(/\s/g, '')

  if (!isPhoneRevealed.value) {
    isPhoneRevealed.value = true
    copy(rawNumber)
  } else {
    const link = document.createElement('a')
    link.href = `tel:${cleanNumber}`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    copy(rawNumber)
  }
}

function enterMotion(delay: number = 0) {
  return {
    initial: { opacity: 0, y: 16 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6, delay }
  }
}

function scrollMotion(delay: number = 0) {
  return {
    initial: { opacity: 0, y: 16 },
    whileInView: { opacity: 1, y: 0 },
    inViewOptions: { once: true, amount: 1 },
    transition: { duration: 0.6, delay }
  }
}

function staggerMotion(index: number = 0) {
  return {
    initial: {opacity: 0},
    whileInView: {opacity: 1},
    inViewOptions: {once: true, amount: 0.2},
    transition: {duration: 0.6, delay: index * 0.08}
  }
}
</script>

<template>
  <div v-if="page">
    <!-- Hero -->
    <UPageHero
      :ui="{
        root: 'pb-0',
        container: 'relative z-10 lg:pt-32 lg:pb-16',
        wrapper: 'flex flex-col items-center',
        title: 'sm:text-6xl lg:text-7xl xl:text-[80px] tracking-tighter leading-[1.05]',
        description: 'mt-5 max-w-xl mx-auto text-base sm:text-lg leading-relaxed text-default',
        links: 'gap-3'
      }"
    >
      <template #top>
        <ClientOnly>
          <Motion v-if="!isBot" v-bind="staggerMotion(0)">
            <LazyHeroShaders class="absolute top-0 inset-x-0 opacity-15 h-full" />
          </Motion>
        </ClientOnly>

        <GradientGlow class="top-0 w-2/3 h-1/2" />
      </template>
      <template #headline>
        <div>
          <NuxtLink to="/" class="flex flex-wrap justify-center gap-6">
            <img src="/ohmelec.webp"
                 alt="Ohmelec - Électricité Générale et Sécurité"
                 width="400"
                 height="160"
                 class="h-40 w-auto"
                 loading="eager"
                 fetchpriority="high"
            />
          </NuxtLink>
          <Motion v-bind="enterMotion(0.2)">
            <UBadge
              color="neutral"
              variant="soft"
              :label="page.hero.headline"
              class="rounded-full px-3 py-1.5 gap-1.5 bg-white/5 backdrop-blur"
            >
              <template #leading>
                <UChip
                  inset
                  standalone
                  :ui="{ base: 'animate-pulse ring-0' }"
                />
              </template>
            </UBadge>
          </Motion>
        </div>
      </template>

      <template #title>
        <Motion
          as="span"
          v-bind="enterMotion(0.35)"
          class="inline-block"
        >
          {{ heroTitle.primary }}
          <br v-if="heroTitle.secondary">
          <span
            v-if="heroTitle.secondary"
            class="animate-shimmer-text"
          >
            {{ heroTitle.secondary }}
          </span>
        </Motion>
      </template>

      <template #description>
        <Motion
          as="span"
          v-bind="enterMotion(0.5)"
          class="inline-block text-white font-medium"
        >
          {{ page.description }}
        </Motion>
      </template>

      <template #links>
        <Motion
          class="flex flex-wrap justify-center gap-6"
          v-bind="enterMotion(0.65)"
        >
          <UButton
            v-for="link in page.hero.links"
            :key="link.label"
            v-bind="link"
          />
        </Motion>
      </template>

      <Motion
        class="max-w-lg mx-auto w-full"
        v-bind="scrollMotion(0.95)"
      >
        <UPageLogos
          :title="page.logos.title"
          :items="page.logos.items"
          :ui="{
            title: 'font-mono uppercase text-xs tracking-[0.12em] text-dimmed text-white',
            logos: 'gap-0',
            logo: 'text-muted size-6'
          }"
        />
      </Motion>
    </UPageHero>

    <!-- Features -->
    <UPageSection
      id="features"
      :ui="{
        root: 'pt-12 pb-24 sm:pt-16 sm:pb-32 scroll-mt-(--ui-header-height)',
        container: 'max-w-5xl',
        headline: 'font-mono font-medium text-xs text-primary uppercase tracking-[0.12em] text-center',
        title: 'max-w-lg mx-auto text-center', // Ajout de text-center
        description: 'max-w-md mx-auto text-dimmed text-center' // Ajout de text-center
      }"
    >
      <template #headline>
        <Motion
          as="span"
          v-bind="scrollMotion()"
          class="inline-block"
        >
          {{ page.features.headline }}
        </Motion>
      </template>

      <template #title>
        <Motion
          as="span"
          v-bind="scrollMotion(0.1)"
          class="inline-block mx-auto"
        >
          {{ page.features.title }}
        </Motion>
      </template>

      <template #description>
        <Motion
          as="span"
          v-bind="scrollMotion(0.2)"
          class="inline-block mx-auto"
        >
          {{ page.features.description }}
        </Motion>
      </template>

      <div class="rounded-2xl border border-default bg-default overflow-hidden">
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px">
          <Motion
            v-for="(feature, index) in page.features.items"
            :key="feature.title"
            v-bind="staggerMotion(index)"
          >
            <UPageCard
              :icon="feature.icon"
              :title="feature.title"
              :description="feature.description"
              class="rounded-none duration-300"
              :ui="{
                root: 'text-center', // Centre les textes de la carte
                leading: 'mx-auto mb-5 flex size-9 justify-center rounded-lg bg-primary/10', // Centre l'icône
                title: 'text-sm tracking-tight',
                description: 'text-sm leading-relaxed sm:line-clamp-2 lg:line-clamp-3 text-dimmed'
              }"
            />
          </Motion>
        </div>
      </div>
    </UPageSection>

    <!-- Secteurs -->
    <UPageSection
      v-if="page?.secteurs"
      id="secteurs"
      :ui="{
        root: 'pt-12 pb-24 sm:pt-16 sm:pb-32 scroll-mt-(--ui-header-height)',
        container: 'max-w-7xl',
        headline: 'font-mono font-medium text-xs text-primary uppercase tracking-[0.12em] text-center',
        title: 'max-w-lg mx-auto text-center',
        description: 'max-w-md mx-auto text-dimmed text-center'
      }"
    >
      <template #headline>
        <Motion
          as="span"
          v-bind="scrollMotion()"
          class="inline-block"
        >
          {{ page.secteurs.headline }}
        </Motion>
      </template>

      <template #title>
        <Motion
          as="span"
          v-bind="scrollMotion(0.1)"
          class="inline-block"
        >
          {{ page.secteurs.title }}
        </Motion>
      </template>

      <template #description>
        <Motion
          as="span"
          v-bind="scrollMotion(0.2)"
          class="inline-block"
        >
          {{ page.secteurs.description }}
        </Motion>
      </template>
      <div class="rounded-2xl border border-default bg-default overflow-hidden mt-12">
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-default">
          <Motion
            v-for="(secteur, index) in page.secteurs.items"
            :key="secteur.title"
            v-bind="staggerMotion(index)"
          >
            <UPageCard
              :icon="secteur.icon"
              :title="secteur.title"
              :description="secteur.description"
              class="rounded-none h-full"
              :ui="{
                root: 'hover:bg-muted/50 transition-colors text-center',
                leading: 'mx-auto mb-4 flex size-10 justify-center rounded-lg bg-primary/10 text-primary',
                title: 'text-lg font-semibold tracking-tight',
                description: 'text-sm leading-relaxed text-dimmed'
              }"
            />
          </Motion>
        </div>
      </div>
    </UPageSection>

    <UPageSection
      v-if="page?.zones"
      id="zones"
      :ui="{
        root: 'pt-12 pb-24 sm:pt-16 sm:pb-32 scroll-mt-(--ui-header-height)',
        container: 'max-w-[1400px]', // Élargi pour donner plus de place aux modules
        headline: 'font-mono font-medium text-xs text-primary uppercase tracking-[0.12em] text-center',
        title: 'max-w-2xl mx-auto text-center',
        description: 'max-w-xl mx-auto text-dimmed text-center'
      }"
    >
      <template #headline>
        <Motion as="span" v-bind="scrollMotion()" class="inline-block">
          {{ page.zones.headline }}
        </Motion>
      </template>

      <template #title>
        <Motion as="span" v-bind="scrollMotion(0.1)" class="inline-block">
          {{ page.zones.title }}
        </Motion>
      </template>

      <template #description>
        <Motion as="span" v-bind="scrollMotion(0.2)" class="inline-block">
          {{ page.zones.description }}
        </Motion>
      </template>

      <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 mt-20">
        <Motion
          v-for="(zone, index) in page.zones.items"
          :key="zone.title"
          v-bind="staggerMotion(index)"
          class="group flex flex-col items-center text-center"
        >
          <div class="relative w-full aspect-[4/3] overflow-hidden rounded-3xl bg-white/5 mb-8 transition-transform duration-700 group-hover:scale-[1.03]">
            <img
              :src="zone.image"
              :alt="zone.title"
              width="600"
              height="450"
              loading="lazy"
              decoding="async"
              class="w-full h-full object-contain scale-95 group-hover:scale-100 transition-transform duration-700"
            />
          </div>

          <h3 class="text-3xl font-bold tracking-tight mb-4">{{ zone.title }}</h3>
          <p class="text-lg text-dimmed leading-relaxed max-w-md">
            {{ zone.description }}
          </p>
        </Motion>
      </div>
    </UPageSection>

    <!-- CTA -->
    <UPageCTA
      id="contact"
      variant="naked"
      :ui="{
        root: 'pt-12 pb-24 sm:pt-16 sm:pb-32 scroll-mt-(--ui-header-height)',
        container: 'max-w-3xl text-center',
        title: 'lg:text-5xl tracking-tighter whitespace-pre-line',
        description: 'mx-auto max-w-sm leading-relaxed text-dimmed'
      }"
    >
      <template #top>
        <GradientGlow class="bottom-0 w-2/3 h-1/2" />
      </template>

      <template #title>
        <Motion
          as="span"
          v-bind="scrollMotion()"
          class="inline-block"
        >
          {{ page.cta.title }}
        </Motion>
      </template>

      <template #description>
        <Motion
          as="span"
          v-bind="scrollMotion(0.1)"
          class="inline-block"
        >
          {{ page.cta.description }}
        </Motion>
      </template>

      <template #links>
        <Motion
          class="flex flex-col items-center justify-center gap-6"
          v-bind="scrollMotion(0.2)"
        >
          <UButton
            v-for="link in page.cta.links"
            :key="link.label"
            v-bind="link"
            size="xl"
          />

          <UButton
            :label="phoneLabel"
            :trailing-icon="copied ? 'i-lucide-copy-check' : (isPhoneRevealed ? 'i-lucide-phone' : 'i-lucide-eye')"
            color="neutral"
            variant="subtle"
            class="font-mono font-light text-toned gap-4 group transition-all duration-300 w-64 justify-center"
            size="xl"
            :ui="{ trailingIcon: 'size-5' }"
            @click="handlePhoneClick"
          />
        </Motion>
      </template>
    </UPageCTA>
  </div>
</template>
